import React, { useState, useEffect } from 'react';
import { FiDownload, FiCalendar, FiFilter, FiLock } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';

const VendorReports = () => {
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [reportType, setReportType] = useState('sales'); // 'sales', 'items', 'purchase', 'users', 'rooms', or 'banquet'
  const [salesReport, setSalesReport] = useState(null);
  const [itemsReport, setItemsReport] = useState(null);
  const [purchaseReport, setPurchaseReport] = useState(null);
  const [usersReport, setUsersReport] = useState(null);
  const [roomsReport, setRoomsReport] = useState(null);
  const [banquetReport, setBanquetReport] = useState(null);
  const [activeFilter, setActiveFilter] = useState('today');
  
  // Set initial date range to today
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  
  const [dateRange, setDateRange] = useState({
    startDate: todayStr,
    endDate: todayStr
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [inventoryCategories, setInventoryCategories] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [filters, setFilters] = useState({
    menuItemId: '',
    categoryId: '',
    subCategoryId: '',
    inventoryCategoryId: '',
    inventoryItemId: '',
    gstFilter: '', // 'withGST', 'withoutGST', or ''
    roomTypeFilter: 'all', // 'all', 'room', 'banquet'
    orderTypeFilter: 'all', // 'all', 'dine_in', 'delivery', 'pickup'
    purchaseTypeFilter: 'all' // 'all', 'inventory', 'others'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Check permission on component mount
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const res = await api.get('/vendor/report-permissions');
        setHasPermission(res.data.data.canViewReports || false);
      } catch (error) {
        console.error('Failed to check permission:', error);
        setHasPermission(false);
      } finally {
        setPermissionLoading(false);
      }
    };

    checkPermission();
  }, []);

  useEffect(() => {
    // Reset filters and active filter when report type changes
    setFilters({
      menuItemId: '',
      categoryId: '',
      subCategoryId: '',
      inventoryCategoryId: '',
      inventoryItemId: '',
      gstFilter: '',
      roomTypeFilter: 'all',
      orderTypeFilter: 'all',
      purchaseTypeFilter: 'all'
    });
    setActiveFilter('today');
    setCurrentPage(1);
    
    // Reset date range to today when switching tabs
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setDateRange({ 
      startDate: todayStr, 
      endDate: todayStr 
    });
  }, [reportType]);

  useEffect(() => {
    if (reportType === 'sales') {
      fetchSalesReport();
    } else if (reportType === 'items') {
      fetchItemsReport();
    } else if (reportType === 'purchase') {
      fetchPurchaseReport();
    } else if (reportType === 'users') {
      fetchUsersReport();
    } else if (reportType === 'rooms') {
      fetchRoomsReport();
    } else if (reportType === 'banquet') {
      fetchBanquetReport();
    }
  }, [dateRange, filters, reportType]);

  useEffect(() => {
    fetchCategories();
    fetchMenuItems();
    fetchInventoryCategories();
    fetchInventoryItems();
  }, []);

  useEffect(() => {
    if (filters.categoryId) {
      fetchSubCategories(filters.categoryId);
    } else {
      setSubCategories([]);
    }
  }, [filters.categoryId]);


  useEffect(() => {
    fetchMenuItems(filters.categoryId,filters.subCategoryId);
  }, [filters.categoryId,filters.subCategoryId]);

  useEffect(() => {
    if (filters.inventoryCategoryId) {
      fetchInventoryItems(filters.inventoryCategoryId);
    } else {
      fetchInventoryItems();
    }
  }, [filters.inventoryCategoryId]);

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = { ...dateRange };
      if (filters.menuItemId) params.menuItemId = filters.menuItemId;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.subCategoryId) params.subCategoryId = filters.subCategoryId;
      if (filters.gstFilter) params.gstFilter = filters.gstFilter;
      // Always send orderType parameter
      if (filters.orderTypeFilter === 'all') {
        params.orderType = 'all'; // Send 'all' for all order types
      } else if (filters.orderTypeFilter) {
        params.orderType = filters.orderTypeFilter; // Send 'dine_in', 'delivery', or 'pickup'
      }

      const response = await api.get('/vendor/reports/sales', { params });
      setSalesReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const fetchItemsReport = async () => {
    setLoading(true);
    try {
      const params = { ...dateRange };
      // Only send category and subcategory filters for items report
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.subCategoryId) params.subCategoryId = filters.subCategoryId;
      // Don't send menuItemId or gstFilter for items report

      const response = await api.get('/vendor/reports/items', { params });
      setItemsReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch items report');
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseReport = async () => {
    setLoading(true);
    try {
      const params = { 
        ...dateRange,
        page: currentPage,
        limit: itemsPerPage
      };
      // Use inventory category and item filters for purchase report
      if (filters.inventoryCategoryId) params.categoryId = filters.inventoryCategoryId;
      if (filters.inventoryItemId) params.inventoryItemId = filters.inventoryItemId;
      // Add purchase type filter
      if (filters.purchaseTypeFilter && filters.purchaseTypeFilter !== 'all') {
        params.purchaseType = filters.purchaseTypeFilter;
      }

      const response = await api.get('/vendor/reports/purchase', { params });
      setPurchaseReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch purchase report');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersReport = async () => {
    setLoading(true);
    try {
      const params = { ...dateRange };
      const response = await api.get('/vendor/reports/users', { params });
      setUsersReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch users report');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomsReport = async () => {
    setLoading(true);
    try {
      const params = { ...dateRange };
      // Always send roomType parameter
      if (filters.roomTypeFilter === 'all') {
        params.roomType = 'all'; // Send 'all' for all room types
      } else if (filters.roomTypeFilter) {
        params.roomType = filters.roomTypeFilter; // Send 'room' or 'banquet'
      }
      const response = await api.get('/vendor/reports/rooms', { params });
      setRoomsReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch rooms report');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanquetReport = async () => {
    setLoading(true);
    try {
      const params = { ...dateRange };
      const response = await api.get('/vendor/reports/banquet', { params });
      setBanquetReport(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch banquet report');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/vendor/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const response = await api.get('/vendor/sub-categories');
      if (response.data.success) {
        const filtered = response.data.data.filter(sub => sub.categoryId._id === categoryId);
        setSubCategories(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch subcategories',categoryId);
    }
  };

  const fetchMenuItems = async (categoryId,subCategoryId) => {
    try {
      const response = await api.get('/vendor/menu-items');
      if (response.data.success) {
        let filtered = response.data.data;
        if (categoryId) {
          filtered = filtered.filter(item => item.categoryId?._id === categoryId);
        }
        if (subCategoryId) {
          filtered = filtered.filter(item => item.subCategoryId?._id === subCategoryId);
        }
        setMenuItems(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch menu items');
    }
  };

  const fetchInventoryCategories = async () => {
    try {
      const response = await api.get('/vendor/inventory-categories');
      if (response.data.success) {
        setInventoryCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch inventory categories');
    }
  };

  const fetchInventoryItems = async (inventoryCategoryId) => {
    try {
      const response = await api.get('/vendor/inventory-items');
      if (response.data.success) {
        let filtered = response.data.data;
        if (inventoryCategoryId) {
          filtered = filtered.filter(item => item.categoryId?._id === inventoryCategoryId);
        }
        setInventoryItems(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch inventory items');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      menuItemId: '',
      categoryId: '',
      subCategoryId: '',
      inventoryCategoryId: '',
      inventoryItemId: '',
      gstFilter: '',
      roomTypeFilter: 'all',
      orderTypeFilter: 'all'
    });
  };

  const hasActiveFilters = filters.menuItemId || filters.categoryId || filters.subCategoryId || filters.inventoryCategoryId || filters.inventoryItemId || filters.gstFilter || (filters.roomTypeFilter && filters.roomTypeFilter !== 'all') || (filters.orderTypeFilter && filters.orderTypeFilter !== 'all');

  const handleQuickFilter = (filter) => {
    setActiveFilter(filter);
    const today = new Date();
    let endDate = today.toISOString().split('T')[0];
    let startDate;

    switch(filter) {
      case 'today':
        startDate = endDate;
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = yesterday.toISOString().split('T')[0];
        endDate = yesterday.toISOString().split('T')[0];
        break;
      case 'last7':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        startDate = last7.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        break;
      default:
        startDate = endDate;
    }

    setDateRange({ startDate, endDate });
  };

  const downloadReport = () => {
    if (reportType === 'sales') {
      downloadSalesReport();
    } else if (reportType === 'items') {
      downloadItemsReport();
    } else if (reportType === 'purchase') {
      downloadPurchaseReport();
    } else if (reportType === 'users') {
      downloadUsersReport();
    } else if (reportType === 'rooms') {
      downloadRoomsReport();
    } else if (reportType === 'banquet') {
      downloadBanquetReport();
    }
  };

  const downloadAllReports = async () => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Downloading all reports...');
      
      // Fetch all report data
      const [salesData, itemsData, purchaseData, usersData, roomsData, banquetData] = await Promise.all([
        api.get('/vendor/reports/sales', { params: dateRange }),
        api.get('/vendor/reports/items', { params: dateRange }),
        api.get('/vendor/reports/purchase', { params: { ...dateRange, page: 1, limit: 1000 } }),
        api.get('/vendor/reports/users', { params: dateRange }),
        api.get('/vendor/reports/rooms', { params: dateRange }),
        api.get('/vendor/reports/banquet', { params: dateRange })
      ]);

      // Create workbook with multiple sheets
      const workbook = XLSX.utils.book_new();
      
      // Add Sales Report Sheet
      if (salesData.data.data) {
        const salesSheet = createSalesSheetData(salesData.data.data);
        const salesWS = XLSX.utils.aoa_to_sheet(salesSheet);
        XLSX.utils.book_append_sheet(workbook, salesWS, 'Sales Report');
      }
      
      // Add Items Report Sheet
      if (itemsData.data.data) {
        const itemsSheet = createItemsSheetData(itemsData.data.data);
        const itemsWS = XLSX.utils.aoa_to_sheet(itemsSheet);
        XLSX.utils.book_append_sheet(workbook, itemsWS, 'Items Report');
      }
      
      // Add Purchase Report Sheet
      if (purchaseData.data.data) {
        const purchaseSheet = createPurchaseSheetData(purchaseData.data.data);
        const purchaseWS = XLSX.utils.aoa_to_sheet(purchaseSheet);
        XLSX.utils.book_append_sheet(workbook, purchaseWS, 'Purchase Report');
      }
      
      // Add Users Report Sheet
      if (usersData.data.data) {
        const usersSheet = createUsersSheetData(usersData.data.data);
        const usersWS = XLSX.utils.aoa_to_sheet(usersSheet);
        XLSX.utils.book_append_sheet(workbook, usersWS, 'Users Report');
      }
      
      // Add Rooms Report Sheet
      if (roomsData.data.data) {
        const roomsSheet = createRoomsSheetData(roomsData.data.data);
        const roomsWS = XLSX.utils.aoa_to_sheet(roomsSheet);
        XLSX.utils.book_append_sheet(workbook, roomsWS, 'Rooms Report');
      }
      
      // Add Banquet Report Sheet
      if (banquetData.data.data) {
        const banquetSheet = createBanquetSheetData(banquetData.data.data);
        const banquetWS = XLSX.utils.aoa_to_sheet(banquetSheet);
        XLSX.utils.book_append_sheet(workbook, banquetWS, 'Banquet Report');
      }

      // Write the file
      XLSX.writeFile(workbook, `all-reports-${dateRange.startDate}-to-${dateRange.endDate}.xlsx`);
      
      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success('All reports downloaded successfully!');
      
    } catch (error) {
      console.error('Error downloading all reports:', error);
      toast.error('Failed to download all reports');
    }
  };

  
  // Helper functions to create sheet data
  const createSalesSheetData = (data) => {
    const sheetData = [
      ['Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Completed Orders', data.completedOrders],
      ['Total Sales', `${data.totalSales}`],
      ['Cash Sales', `${data.totalCashSales || 0}`],
      ['Online Sales', `${data.totalOnlineSales || 0}`],
      ['Total GST', `${data.totalGST}`],
      [],
      ['Orders by Type'],
      ['Dine-in', data.ordersByType['dine-in']],
      ['Delivery', data.ordersByType['delivery']],
      ['Pickup', data.ordersByType['pickup']],
      ['Vehicle', data.ordersByType['vehicle']],
      [],
      ['Order Details'],
      ['Order Number', 'Date', 'Customer', 'Type', 'Customer GST Number', 'Subtotal','Discount','GST Amount','Total Amount','Status']
    ];

    data.orders.forEach(order => {
      sheetData.push([
        order.orderNumber,
        new Date(order.createdAt).toLocaleDateString(),
        order.customerId?.fullName || 'Guest',
        order.orderType,
        order.gstNumber || 'N/A',
        order.pricing?.subtotal || 0,
        order.pricing?.discount || 0,
        order.pricing?.gstAmount || 0,
        order.pricing?.total || 0,
        order.status
      ]);
    });

    return sheetData;
  };

  const createItemsSheetData = (data) => {
    const sheetData = [
      ['Item-wise Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Items Sold', data.totalItems],
      ['Total Quantity', data.totalQuantity],
      [],
      ['Item Details'],
      ['Item Name', 'Category', 'Sub Category', 'Quantity Sold', 'Price', '% of Total']
    ];

    data.items.forEach(item => {
      sheetData.push([
        item.itemName,
        item.category || 'N/A',
        item.subCategory || 'N/A',
        item.quantitySold,
        `${item.price}`,
        `${item.percentageOfTotal.toFixed(1)}%`
      ]);
    });

    return sheetData;
  };

  const createPurchaseSheetData = (data) => {
    const sheetData = [
      ['Purchase Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Purchases', `${data.totalPurchases}`],
      ['Total Amount', `${data.totalAmount}`],
      [],
      ['Purchase Details'],
      ['Date', 'Item Name', 'Category', 'Quantity', 'Cost Per Unit', 'Total Cost', 'Notes']
    ];

    data.purchases.forEach(purchase => {
      sheetData.push([
        new Date(purchase.createdAt).toLocaleDateString(),
        purchase.itemId?.name || 'N/A',
        purchase.itemId?.categoryId?.name || 'N/A',
        purchase.quantity,
        `${purchase.costPerUnit}`,
        `${purchase.totalCost}`,
        purchase.notes || '-'
      ]);
    });

    return sheetData;
  };

  const createUsersSheetData = (data) => {
    const sheetData = [
      ['User Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Customers', data.totalCustomers],
      ['Total Orders', data.totalOrders],
      ['Total Revenue', `${data.totalRevenue}`],
      ['Average Revenue Per Customer', `${data.averageRevenuePerCustomer?.toFixed(2)}`],
      [],
      ['User Details'],
      ['Customer Name', 'Phone', 'Total Orders', 'Total Revenue', 'Avg Order Value', 'Dine-in', 'Delivery', 'Pickup', 'Vehicle', 'Total Visits', 'First Visit', 'Last Visit']
    ];

    data.users.forEach(user => {
      sheetData.push([
        user.customerName,
        user.customerPhone,
        user.totalOrders,
        `${user.totalRevenue}`,
        `${user.averageOrderValue?.toFixed(2)}`,
        user.dineInCount,
        user.deliveryCount,
        user.pickupCount,
        user.vehicleCount || 0,
        user.totalVisits,
        new Date(user.firstVisit).toLocaleDateString(),
        new Date(user.lastVisit).toLocaleDateString()
      ]);
    });

    return sheetData;
  };

  const createRoomsSheetData = (data) => {
    const sheetData = [
      ['Room Checkout Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Bookings Rooms', data.totalBookings || 0],
      ['Total Checked In Rooms', data.totalCheckedInRooms || 0],
      ['Total Checked Out Rooms', data.totalCheckedOutRooms || 0],
      ['Total booked Rooms', data.totalBookedRooms || 0],
      ['Total Cancelled Rooms', data.totalCancelledRooms || 0],
      ['Room Revenue', (data.totalRoomRevenue || 0).toFixed(2)],
      ['GST Amount', (data.totalGSTAmount || 0).toFixed(2)],
      // ['Other Amount', (data.totalOtherAmount || 0).toFixed(2)],
      ['Total Revenue', (data.totalRevenue || 0).toFixed(2)],
      ['Average Stay Duration', `${data.averageStayDays} Days`],
      [],
      ['Booking Details'],
      ['Room Details', 'Guest & Status', 'Stay Period', 'Room Amount', 'GST Amount', 'Adjustments', 'Final Amount', 'Paid Amount', 'Cash', 'Card', 'UPI', 'Payment Status']
    ];

    data.roomDetails.forEach(room => {
      sheetData.push([
        `${room.roomNumber} - ${room.roomType || 'Standard'}`,
        `${room.guestName || '-'} (${room.status?.replace('_', ' ') || 'checked_out'})`,
        `${room.checkIn ? new Date(room.checkIn).toLocaleDateString() : '-'} to ${room.checkOut ? new Date(room.checkOut).toLocaleDateString() : '-'}`,
        (room.totalAmount || 0).toFixed(2),
        (room.gstAmount || 0).toFixed(2),
        (room.otherAmount || 0).toFixed(2),
        `"Tip: ${(room.tipAmount || 0).toFixed(2)}, Discount: -${(room.discountAmount || 0).toFixed(2)}"`,
        (room.totalFinalAmount || 0).toFixed(2),
        (room.paidAmount || 0).toFixed(2),
        (room.paymentModeBreakdown?.cash || 0).toFixed(2),
        (room.paymentModeBreakdown?.card || 0).toFixed(2),
        (room.paymentModeBreakdown?.upi || 0).toFixed(2),
        room.paymentStatus || 'pending'
      ]);
    });

    return sheetData;
  };

  const createBanquetSheetData = (data) => {
    const sheetData = [
      ['Banquet Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Packages', data.totalPackages || 0],
      ['Total Revenue', (data.totalRevenue || 0).toFixed(2)],
      ['Average GST Percentage', `${data.avgGstPercentage || 0}%`],
      ['Average Price Per Pack', Number(data.avgPricePerPack || 0).toFixed(2)],
      [],
      ['Banquet Details'],
      ['Bill No', 'Customer Name', 'GST Number', 'Banquet Name', 'Packages', 'Price/Pack', 'Subtotal', 'GST %', 'GST Amount', 'Discount', 'Total', 'Date']
    ];

    data.banquets?.forEach(banquet => {
      sheetData.push([
        banquet.billNo || 'N/A',
        banquet.customerName || 'N/A',
        banquet.gstNumber || 'N/A',
        banquet.banquetName || 'N/A',
        banquet.noOfPacks || 0,
        (banquet.pricePerPack || 0).toFixed(2),
        (banquet.subtotal || 0).toFixed(2),
        `${banquet.gstPercentage || 0}%`,
        (banquet.gstAmount || 0).toFixed(2),
        (banquet.discountAmount || 0).toFixed(2),
        (banquet.total || 0).toFixed(2),
        new Date(banquet.createdAt).toLocaleDateString('en-IN')
      ]);
    });

    return sheetData;
  };

  const downloadSalesReport = () => {
    if (!salesReport) return;
    
    const csvContent = [
      ['Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Completed Orders', salesReport.completedOrders],
      ['Total Sales', `${salesReport.totalSales}`],
      ['Cash Sales', `${salesReport.totalCashSales || 0}`],
      ['Online Sales', `${salesReport.totalOnlineSales || 0}`],
      ['Total GST', `${salesReport.totalGST}`],
      [],
      ['Orders by Type'],
      ['Dine-in', salesReport.ordersByType['dine-in']],
      ['Delivery', salesReport.ordersByType['delivery']],
      ['Pickup', salesReport.ordersByType['pickup']],
      ['Vehicle', salesReport.ordersByType['vehicle']],
      [],
      ['Order Details'],
      ['Order Number', 'Date', 'Customer', 'Type', 'Customer GST Number', 'Subtotal','Discount','GST Amount','Total Amount','Status']
    ];

    salesReport.orders.forEach(order => {
      csvContent.push([
        order.orderNumber,
        new Date(order.createdAt).toLocaleDateString(),
        order.customerId?.fullName || 'Guest',
        order.orderType,
        order.gstNumber || 'N/A',
        order.pricing?.subtotal || 0,
        order.pricing?.discount || 0,
        order.pricing?.gstAmount || 0,
        order.pricing?.total || 0,
        order.status
      ]);
    });

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sales-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    toast.success('Sales report downloaded');
  };

  const downloadItemsReport = () => {
    if (!itemsReport) return;
    
    const csvContent = [
      ['Item-wise Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Items Sold', itemsReport.totalItems],
      ['Total Quantity', itemsReport.totalQuantity],
      [],
      ['Item Details'],
      ['Item Name', 'Category', 'Sub Category', 'Quantity Sold', 'Price', '% of Total']
    ];

    itemsReport.items.forEach(item => {
      csvContent.push([
        item.itemName,
        item.category || 'N/A',
        item.subCategory || 'N/A',
        item.quantitySold,
        `${item.price}`,
        `${item.percentageOfTotal.toFixed(1)}%`
      ]);
    });

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `items-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    toast.success('Items report downloaded');
  };

  const downloadPurchaseReport = () => {
    if (!purchaseReport) return;
    
    const csvContent = [
      ['Purchase Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Purchases', `${purchaseReport.totalPurchases}`],
      ['Total Amount', `${purchaseReport.totalAmount}`],
      [],
      ['Purchase Details'],
      ['Date', 'Item Name', 'Category', 'Quantity', 'Cost Per Unit', 'Total Cost', 'Notes']
    ];

    purchaseReport.purchases.forEach(purchase => {
      csvContent.push([
        new Date(purchase.createdAt).toLocaleDateString(),
        purchase.itemId?.name || 'N/A',
        purchase.itemId?.categoryId?.name || 'N/A',
        purchase.quantity,
        `${purchase.costPerUnit}`,
        `${purchase.totalCost}`,
        purchase.notes || '-'
      ]);
    });

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `purchase-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    toast.success('Purchase report downloaded');
  };

  const downloadUsersReport = () => {
    if (!usersReport) return;
    
    const csvContent = [
      ['User Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Customers', usersReport.totalCustomers],
      ['Total Orders', usersReport.totalOrders],
      ['Total Revenue', `${usersReport.totalRevenue}`],
      ['Average Revenue Per Customer', `${usersReport.averageRevenuePerCustomer?.toFixed(2)}`],
      [],
      ['User Details'],
      ['Customer Name', 'Phone', 'Total Orders', 'Total Revenue', 'Avg Order Value', 'Dine-in', 'Delivery', 'Pickup', 'Vehicle', 'Total Visits', 'First Visit', 'Last Visit']
    ];

    usersReport.users.forEach(user => {
      csvContent.push([
        user.customerName,
        user.customerPhone,
        user.totalOrders,
        `${user.totalRevenue}`,
        `${user.averageOrderValue?.toFixed(2)}`,
        user.dineInCount,
        user.deliveryCount,
        user.pickupCount,
        user.vehicleCount || 0,
        user.totalVisits,
        new Date(user.firstVisit).toLocaleDateString(),
        new Date(user.lastVisit).toLocaleDateString()
      ]);
    });

    const csv = csvContent.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    toast.success('Users report downloaded');
  };

  const downloadRoomsReport = () => {
    if (!roomsReport) return;
    
    const csvContent = [
      ['Room Checkout Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Bookings Rooms', roomsReport.totalBookings || 0],
      ['Total Checked In Rooms', roomsReport.totalCheckedInRooms || 0],
      ['Total Checked Out Rooms', roomsReport.totalCheckedOutRooms || 0],
      ['Total booked Rooms', roomsReport.totalBookedRooms || 0],
      ['Total Cancelled Rooms', roomsReport.totalCancelledRooms || 0],
      ['Room Revenue', (roomsReport.totalRoomRevenue || 0).toFixed(2)],
      ['GST Amount', (roomsReport.totalGSTAmount || 0).toFixed(2)],
      // ['Other Amount', (roomsReport.totalOtherAmount || 0).toFixed(2)],
      ['Total Revenue', (roomsReport.totalRevenue || 0).toFixed(2)],
      ['Average Stay Duration', `${roomsReport.averageStayDays} Days`],
      [],
      ['Booking Details'],
      ['Room Details', 'Guest & Status', 'Stay Period', 'Room Amount', 'GST Amount', 'Adjustments', 'Final Amount', 'Paid Amount', 'Cash', 'Card', 'UPI', 'Payment Status']
    ];

    roomsReport.roomDetails.forEach(room => {
      csvContent.push([
        `${room.roomNumber} - ${room.roomType || 'Standard'}`,
        `${room.guestName || '-'} (${room.status?.replace('_', ' ') || 'checked_out'})`,
        `${room.checkIn ? new Date(room.checkIn).toLocaleDateString() : '-'} to ${room.checkOut ? new Date(room.checkOut).toLocaleDateString() : '-'}`,
        (room.totalAmount || 0).toFixed(2),
        (room.gstAmount || 0).toFixed(2),
        (room.otherAmount || 0).toFixed(2),
        `"Tip: ${(room.tipAmount || 0).toFixed(2)}, Discount: -${(room.discountAmount || 0).toFixed(2)}"`,
        (room.totalFinalAmount || 0).toFixed(2),
        (room.paidAmount || 0).toFixed(2),
        (room.paymentModeBreakdown?.cash || 0).toFixed(2),
        (room.paymentModeBreakdown?.card || 0).toFixed(2),
        (room.paymentModeBreakdown?.upi || 0).toFixed(2),
        room.paymentStatus || 'pending'
      ]);
    });

    const blob = new Blob([csvContent.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `room-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Room report downloaded');
  };

  const downloadBanquetReport = () => {
    if (!banquetReport) return;
    
    const csvContent = [
      ['Banquet Sales Report', `${dateRange.startDate} to ${dateRange.endDate}`],
      [],
      ['Summary'],
      ['Total Packages', banquetReport.totalPackages || 0],
      ['Total Revenue', (banquetReport.totalRevenue || 0).toFixed(2)],
      ['Average GST Percentage', `${banquetReport.avgGstPercentage || 0}%`],
      ['Average Price Per Pack', Number(banquetReport.avgPricePerPack || 0).toFixed(2)],
      [],
      ['Banquet Details'],
      ['Bill No', 'Customer Name', 'GST Number', 'Banquet Name', 'Packages', 'Price/Pack', 'Subtotal', 'GST %', 'GST Amount', 'Discount', 'Total', 'Date']
    ];

    banquetReport.banquets?.forEach(banquet => {
      csvContent.push([
        banquet.billNo || 'N/A',
        banquet.customerName || 'N/A',
        banquet.gstNumber || 'N/A',
        banquet.banquetName || 'N/A',
        banquet.noOfPacks || 0,
        (banquet.pricePerPack || 0).toFixed(2),
        (banquet.subtotal || 0).toFixed(2),
        `${banquet.gstPercentage || 0}%`,
        (banquet.gstAmount || 0).toFixed(2),
        (banquet.discountAmount || 0).toFixed(2),
        (banquet.total || 0).toFixed(2),
        new Date(banquet.createdAt).toLocaleDateString('en-IN')
      ]);
    });

    const blob = new Blob([csvContent.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `banquet-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Banquet report downloaded');
  };

  if (permissionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-500"></div>
      </div>
    );
  }

  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <FiLock className="text-red-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to access reports.</p>
          <p className="text-sm text-gray-500">Please contact your Vendor Owner to grant access.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 p-4 md:p-6">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Business Reports</h1>
              <p className="text-gray-600">Analyze your sales performance and item analytics</p>
            </div>
            <div className="flex gap-3">
              <button 
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center space-x-2"
                onClick={downloadAllReports}
              >
                <FiDownload className="text-lg" />
                <span>All Reports</span>
              </button>
              <button 
                className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center space-x-2"
                onClick={downloadReport}
              >
                <FiDownload className="text-lg" />
                <span>Download Report</span>
              </button>
            </div>
          </div>
        </div>

        {/* Report Type Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            <button
              onClick={() => setReportType('sales')}
              className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                reportType === 'sales' 
                  ? 'border-red-500 text-red-600 bg-white' 
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
              }`}
            >
              <span className="text-base">📈</span>
              <span className="text-xs">Vendor Sales</span>
            </button>
            <button
              onClick={() => setReportType('rooms')}
              className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                reportType === 'rooms' 
                  ? 'border-red-500 text-red-600 bg-white' 
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
              }`}
            >
              <span className="text-base">🏠</span>
              <span className="text-xs">Room Sales</span>
            </button>

            <button
              onClick={() => setReportType('banquet')}
              className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                reportType === 'banquet' 
                  ? 'border-red-500 text-red-600 bg-white' 
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
              }`}
            >
              <span className="text-base">🎊</span>
              <span className="text-xs">Banquet Sales</span>
            </button>
            
            <button
              onClick={() => setReportType('items')}
              className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                reportType === 'items' 
                  ? 'border-red-500 text-red-600 bg-white' 
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
              }`}
            >
              <span className="text-base">🍽️</span>
              <span className="text-xs">Item-wise Sales</span>
            </button>
            <button
              onClick={() => setReportType('purchase')}
              className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                reportType === 'purchase' 
                  ? 'border-red-500 text-red-600 bg-white' 
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
              }`}
            >
              <span className="text-base">💰</span>
              <span className="text-xs">Purchase</span>
            </button>
            <button
              onClick={() => setReportType('users')}
              className={`px-3 py-2 rounded-t-lg font-semibold transition-all duration-300 flex items-center justify-center gap-1 border-b-2 ${
                reportType === 'users' 
                  ? 'border-red-500 text-red-600 bg-white' 
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-800'
              }`}
            >
              <span className="text-base">👥</span>
              <span className="text-xs">Users</span>
            </button>
            
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-3 items-center flex-wrap">
            <button 
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeFilter === 'today' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              onClick={() => handleQuickFilter('today')}
            >
              Today
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeFilter === 'yesterday' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              onClick={() => handleQuickFilter('yesterday')}
            >
              Yesterday
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeFilter === 'last7' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              onClick={() => handleQuickFilter('last7')}
            >
              Last 7 Days
            </button>
            <button 
              className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                activeFilter === 'thisMonth' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              onClick={() => handleQuickFilter('thisMonth')}
            >
              This Month
            </button>
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                value={dateRange.startDate}
                onChange={(e) => {
                  setDateRange({ ...dateRange, startDate: e.target.value });
                  setActiveFilter('custom');
                }}
                placeholder="Start Date"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                className="px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                value={dateRange.endDate}
                onChange={(e) => {
                  setDateRange({ ...dateRange, endDate: e.target.value });
                  setActiveFilter('custom');
                }}
                placeholder="End Date"
              />
            </div>
            {
              (reportType !== "sales" && reportType !== "users" && reportType !== "rooms" && reportType !== "banquet") && (
                <button 
                  className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                    showFilters 
                      ? 'bg-red-100 text-red-700 border-2 border-red-200' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FiFilter className="text-lg" />
                  <span>{showFilters ? 'Hide' : 'Show'} Advanced Filters</span>
                  {hasActiveFilters && (
                    <span className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold animate-pulse">
                      {Object.values(filters).filter(v => v).length}
                    </span>
                  )}
                </button>
              )
            }
            {
              reportType === 'sales' && (
                <div className="flex gap-2">
                  <select
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                    value={filters.gstFilter}
                    onChange={(e) => handleFilterChange('gstFilter', e.target.value)}
                  >
                    <option value="">All Orders</option>
                    <option value="withGST">With Customer GST</option>
                    <option value="withoutGST">Without Customer GST</option>
                  </select>
                  <select
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                    value={filters.orderTypeFilter}
                    onChange={(e) => handleFilterChange('orderTypeFilter', e.target.value)}
                  >
                    <option value="all">All Order Types</option>
                    <option value="dine-in">Dine In</option>
                    <option value="delivery">Delivery</option>
                    <option value="pickup">Pickup</option>
                    <option value="vehicle">Vehicle</option>
                  </select>
                </div>
              )
            }
            {
              reportType === 'rooms' && (
                <div>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                    value={filters.roomTypeFilter}
                    onChange={(e) => handleFilterChange('roomTypeFilter', e.target.value)}
                  >
                    <option value="all">All Rooms</option>
                    <option value="room">Rooms Only</option>
                    <option value="banquet">Banquet Only</option>
                  </select>
                </div>
              )
            }
          </div>

          {/* Advanced Filters */}
          {showFilters && reportType !== 'sales' && reportType !== 'users' && reportType !== "rooms" && (
            <div className="bg-gray-50 rounded-lg p-4 mt-4 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {reportType === 'purchase' ? (
                  <>
                    {/* Purchase Report - Inventory Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                        value={filters.inventoryCategoryId}
                        onChange={(e) => {
                          handleFilterChange('inventoryCategoryId', e.target.value);
                          handleFilterChange('inventoryItemId', '');
                        }}
                      >
                        <option value="">All Categories</option>
                        {inventoryCategories.map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Purchase Report - Inventory Item */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Inventory Item</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                        value={filters.inventoryItemId}
                        onChange={(e) => handleFilterChange('inventoryItemId', e.target.value)}
                      >
                        <option value="">All Items</option>
                        {inventoryItems.map(item => (
                          <option key={item._id} value={item._id}>{item.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Purchase Report - Purchase Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Type</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                        value={filters.purchaseTypeFilter}
                        onChange={(e) => handleFilterChange('purchaseTypeFilter', e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="inventory">Inventory Only</option>
                        <option value="others">Others Only</option>
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Items Report - Menu Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                        value={filters.categoryId}
                        onChange={(e) => {
                          handleFilterChange('categoryId', e.target.value);
                          handleFilterChange('subCategoryId', '');
                        }}
                      >
                        <option value="">All Categories</option>
                        {categories.map(cat => (
                          <option key={cat._id} value={cat._id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Items Report - Sub Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Sub Category</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        value={filters.subCategoryId}
                        onChange={(e) => handleFilterChange('subCategoryId', e.target.value)}
                        disabled={!filters.categoryId}
                      >
                        <option value="">All Sub Categories</option>
                        {subCategories.map(sub => (
                          <option key={sub._id} value={sub._id}>{sub.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Items Report - Menu Item */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Menu Item</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                        value={filters.menuItemId}
                        onChange={(e) => handleFilterChange('menuItemId', e.target.value)}
                      >
                        <option value="">All Menu Items</option>
                        {menuItems.map(item => (
                          <option key={item._id} value={item._id}>{item.name}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {hasActiveFilters && (
                  <div className="mt-4 flex justify-end">
                    <button 
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg font-medium transition-colors duration-200" 
                      onClick={clearFilters}
                    >
                      Clear All Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {reportType === 'sales' && !salesReport ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No sales data available</h3>
            <p className="text-gray-600 mb-6">Select a date range to view sales report</p>
          </div>
        ) : reportType === 'sales' && salesReport && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FiCalendar className="text-blue-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-600">{salesReport.totalOrders}</h3>
                    <p className="text-gray-600">Total Orders</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FiCalendar className="text-purple-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-purple-600">₹{salesReport.totalSales?.toLocaleString()}</h3>
                    <p className="text-gray-600">Total Sales</p>
                    {(salesReport.totalCashSales > 0 || salesReport.totalOnlineSales > 0) && (
                      <div className="text-xs mt-2 space-y-1">
                        {salesReport.totalCashSales > 0 && (
                          <div className="text-green-600 font-medium">💵 Cash: ₹{salesReport.totalCashSales?.toLocaleString() || 0}</div>
                        )}
                        {salesReport.totalOnlineSales > 0 && (
                          <div className="text-blue-600 font-medium">💳 Online: ₹{salesReport.totalOnlineSales?.toLocaleString() || 0}</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <FiCalendar className="text-orange-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-orange-600">₹{salesReport.totalGST?.toLocaleString()}</h3>
                    <p className="text-gray-600">Total GST</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Orders by Type */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Orders by Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-green-700">{salesReport.ordersByType['dine-in']}</h3>
                    <p className="text-green-700">Dine-in Orders</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-blue-700">{salesReport.ordersByType['delivery']}</h3>
                    <p className="text-blue-700">Delivery Orders</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-purple-700">{salesReport.ordersByType['pickup']}</h3>
                    <p className="text-purple-700">Pickup Orders</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-orange-700">{salesReport.ordersByType['vehicle']}</h3>
                    <p className="text-orange-700">Vehicle Orders</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-red-50 to-red-100 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Order #</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">GST Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {salesReport.orders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((order) => (
                      <tr key={order._id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{order.orderNumber}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{new Date(order.createdAt).toLocaleDateString()}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{order.customerId?.fullName || 'Guest'}</div>
                            {order.isMc && (
                              <div className="mt-1">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  MC
                                </span>
                              </div>
                            )}
                            {order.complimentary && (
                              <div className="mt-1 text-xs text-gray-600 max-w-[200px] truncate" title={order.complimentary}>
                                🎁 {order.complimentary}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {order.orderType}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          {order.gstNumber ? (
                            <span className="text-xs font-medium text-green-600">
                              {order.gstNumber}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <div className="font-bold text-gray-900">₹{order.pricing?.total || 0}</div>
                            <div className="text-xs text-gray-500">
                              GST: ₹{order.pricing?.gstAmount || 0}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === 'completed' ? 'bg-green-100 text-green-800' :
                            order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {Math.ceil(salesReport.orders.length / itemsPerPage) > 1 && (
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="text-sm text-gray-600">
                      Showing {Math.min((currentPage - 1) * itemsPerPage + 1, salesReport.orders.length)}-{Math.min(currentPage * itemsPerPage, salesReport.orders.length)} of {salesReport.orders.length} orders
                    </div>
                    <div className="flex gap-2 items-center">
                      <button
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-medium transition-colors duration-200"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      {[...Array(Math.ceil(salesReport.orders.length / itemsPerPage))].map((_, idx) => {
                        const pageNum = idx + 1;
                        const totalReportPages = Math.ceil(salesReport.orders.length / itemsPerPage);
                        if (
                          pageNum === 1 ||
                          pageNum === totalReportPages ||
                          (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              className={`min-w-[36px] px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                                pageNum === currentPage 
                                  ? 'bg-red-500 text-white' 
                                  : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700'
                              }`}
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                          return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                        }
                        return null;
                      })}
                      <button
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-medium transition-colors duration-200"
                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(salesReport.orders.length / itemsPerPage), prev + 1))}
                        disabled={currentPage === Math.ceil(salesReport.orders.length / itemsPerPage)}
                      >
                        Next
                      </button>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-600">Show</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:border-red-500 focus:outline-none transition-colors duration-200"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-gray-600">per page</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {reportType === 'sales' && salesReport && salesReport.orders && salesReport.orders.length === 0 && (
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-600">No orders found for the selected date range</p>
          </div>
        )}

        {/* Banquet Report */}
        {reportType === 'banquet' && !banquetReport ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No banquet data available</h3>
            <p className="text-gray-600 mb-6">Select a date range to view banquet sales report</p>
          </div>
        ) : reportType === 'banquet' && banquetReport && (
          <>
            {/* Banquet Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">📦</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-600">{banquetReport.totalPackages || 0}</h3>
                    <p className="text-gray-600">Total Packages</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-600">₹{(banquetReport.totalRevenue || 0).toLocaleString('en-IN')}</h3>
                    <p className="text-gray-600">Total Revenue</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">📊</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-purple-600">{banquetReport.avgGstPercentage || 0}%</h3>
                    <p className="text-gray-600">Avg GST %</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">💵</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-orange-600">₹{Number(banquetReport.avgPricePerPack || 0).toLocaleString('en-IN')}</h3>
                    <p className="text-gray-600">Avg Price/Pack</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Banquet Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bill & Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer Details</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Banquet Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Packages & Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST %</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {banquetReport.banquets?.map((banquet, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            <div className="text-sm font-medium text-gray-900">{banquet.billNo || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{new Date(banquet.createdAt).toLocaleDateString('en-IN')}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-start space-y-2">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 text-xs font-bold">👤</span>
                              </div>
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900 text-sm">{banquet.customerName || 'N/A'}</div>
                                {banquet.gstNumber && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">
                                      GST: {banquet.gstNumber}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{banquet.banquetName || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-900">{banquet.noOfPacks || 0}</span>
                              <span className="text-xs text-gray-500">×</span>
                              <span className="text-sm font-semibold text-blue-600">₹{(banquet.pricePerPack || 0).toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-gray-500">
                              Total: ₹{((banquet.noOfPacks || 0) * (banquet.pricePerPack || 0)).toFixed(2)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">₹{(banquet.subtotal || 0).toFixed(2)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{banquet.gstPercentage || 0}%</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">₹{(banquet.gstAmount || 0).toFixed(2)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-green-600">-₹{(banquet.discountAmount || 0).toFixed(2)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-bold text-blue-600">₹{(banquet.total || 0).toFixed(2)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Items Report */}
        {reportType === 'items' && !itemsReport ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No items data available</h3>
            <p className="text-gray-600 mb-6">Select a date range to view item-wise sales report</p>
          </div>
        ) : reportType === 'items' && itemsReport && (
          <>
            {/* Items Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">🍽️</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-orange-600">{itemsReport.totalItems || 0}</h3>
                    <p className="text-gray-600">Total Items Sold</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">📦</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-600">{itemsReport.totalQuantity || 0}</h3>
                    <p className="text-gray-600">Total Quantity</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-red-50 to-red-100 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity Sold</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {itemsReport.items && itemsReport.items.length > 0 ? (
                      itemsReport.items.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{item.itemName}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {item.category || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-blue-600">{item.quantitySold}</div>
                          </td>
                          <td className="px-6 py-4 text-gray-700">₹{item.price?.toLocaleString() || 0}</td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                              {item.percentageOfTotal?.toFixed(1) || 0}%
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl text-gray-400">📦</span>
                          </div>
                          <p className="text-gray-600 font-medium">No items sold in the selected date range</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      {/* Purchase Report */}
        {reportType === 'purchase' && !purchaseReport ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No purchase data available</h3>
            <p className="text-gray-600 mb-6">Select a date range to view purchase report</p>
          </div>
        ) : reportType === 'purchase' && purchaseReport && (
          <>
            {/* Purchase Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-600">{purchaseReport.totalPurchases || 0}</h3>
                    <p className="text-gray-600">Total Purchases</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">💵</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-600">₹{purchaseReport.totalAmount?.toLocaleString() || 0}</h3>
                    <p className="text-gray-600">Total Amount</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Purchases Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-red-50 to-red-100 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Item Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Quantity</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Cost Per Unit</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Total Cost</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {purchaseReport.purchases && purchaseReport.purchases.length > 0 ? (
                      purchaseReport.purchases.map((purchase, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {new Date(purchase.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{purchase.itemId?.name || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                              {purchase.itemId?.categoryId?.name || 'N/A'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{purchase.quantity}</div>
                          </td>
                          <td className="px-6 py-4 text-gray-700">₹{purchase.costPerUnit?.toLocaleString() || 0}</td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-green-600">₹{purchase.totalCost?.toLocaleString() || 0}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600 max-w-xs truncate" title={purchase.notes}>
                              {purchase.notes || '-'}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center">
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl text-gray-400">💰</span>
                          </div>
                          <p className="text-gray-600 font-medium">No purchases found in the selected date range</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            {purchaseReport.purchases && purchaseReport.purchases.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mt-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                    {Math.min(currentPage * itemsPerPage, purchaseReport.totalPurchases || 0)} of{' '}
                    {purchaseReport.totalPurchases || 0} purchases
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-200"
                    >
                      Previous
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, Math.ceil((purchaseReport.totalPurchases || 0) / itemsPerPage)) }, (_, i) => {
                        const totalPages = Math.ceil((purchaseReport.totalPurchases || 0) / itemsPerPage);
                        const startPage = Math.max(1, currentPage - 2);
                        const endPage = Math.min(totalPages, startPage + 4);
                        const page = startPage + i;
                        
                        if (page <= endPage) {
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors duration-200 ${
                                currentPage === page
                                  ? 'bg-red-500 text-white'
                                  : 'border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        }
                        return null;
                      })}
                    </div>
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil((purchaseReport.totalPurchases || 0) / itemsPerPage), prev + 1))}
                      disabled={currentPage >= Math.ceil((purchaseReport.totalPurchases || 0) / itemsPerPage)}
                      className="px-3 py-1 rounded-lg border border-gray-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-200"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* User Report */}
        {reportType === 'users' && !usersReport ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No user data available</h3>
            <p className="text-gray-600 mb-6">Select a date range to view user report</p>
          </div>
        ) : reportType === 'users' && usersReport && (
          <>
            {/* User Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">👥</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-blue-600">{usersReport.totalCustomers || 0}</h3>
                    <p className="text-gray-600">Total Customers</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">📦</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-600">{usersReport.totalOrders || 0}</h3>
                    <p className="text-gray-600">Total Orders</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">💰</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-purple-600">₹{usersReport.totalRevenue?.toLocaleString() || 0}</h3>
                    <p className="text-gray-600">Total Revenue</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                    <span className="text-xl">📊</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-orange-600">₹{usersReport.averageRevenuePerCustomer?.toLocaleString() || 0}</h3>
                    <p className="text-gray-600">Avg Revenue/Customer</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-red-50 to-red-100 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Phone</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Orders</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Avg Order</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Order Types</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Visits</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Last Visit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usersReport.users && usersReport.users.length > 0 ? (
                      usersReport.users.map((user, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors duration-150">
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{user.customerName}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">{user.customerPhone}</td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-blue-600">{user.totalOrders}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-green-600">₹{user.totalRevenue?.toLocaleString() || 0}</div>
                            <div className="text-xs text-gray-500">
                              {user.percentageOfRevenue?.toFixed(1)}% of total
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-700">₹{user.averageOrderValue?.toLocaleString() || 0}</td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {user.orderTypes && user.orderTypes.map((orderType, index) => (
                                <span 
                                  key={index}
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    orderType.type === 'dine-in' ? 'bg-green-100 text-green-800' :
                                    orderType.type === 'delivery' ? 'bg-blue-100 text-blue-800' :
                                    orderType.type === 'pickup' ? 'bg-purple-100 text-purple-800' :
                                    orderType.type === 'vehicle' ? 'bg-orange-100 text-orange-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {orderType.typeName}: {orderType.count}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-indigo-600">{user.totalVisits}</div>
                            <div className="text-xs text-gray-500">
                              Since {new Date(user.firstVisit).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            {new Date(user.lastVisit).toLocaleDateString()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center">
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl text-gray-400">👥</span>
                          </div>
                          <p className="text-gray-600 font-medium">No users found in the selected date range</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Rooms Report */}
        {reportType === 'rooms' && !roomsReport ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiCalendar className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No checkout data available</h3>
            <p className="text-gray-600 mb-6">Select a date range to view room checkout report</p>
          </div>
        ) : reportType === 'rooms' && roomsReport && (
          <>
            {/* Rooms Summary */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 mb-6">

              {/* Total Bookings */}
              <div className="bg-white rounded-lg border p-4 flex flex-col justify-between h-full">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center text-lg">
                    📅
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-yellow-600 leading-none">
                      {roomsReport.totalBookings || 0}
                    </h3>
                    <p className="text-xs text-gray-500">Total Bookings</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></div>
                    <span className="text-gray-600">Booked: {roomsReport.totalBookedRooms || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600">Check-in: {roomsReport.totalCheckedInRooms || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    <span className="text-gray-600">Check-out: {roomsReport.totalCheckedOutRooms || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                    <span className="text-gray-600">Cancelled: {roomsReport.totalCancelledRooms || 0}</span>
                  </div>
                </div>
              </div>

              {/* Room Revenue & GST */}
              <div className="bg-white rounded-lg border p-4 flex flex-col justify-between h-full">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-lg">
                    💰
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-purple-600 leading-none">
                      ₹{(roomsReport.totalRoomRevenue || 0).toLocaleString('en-IN')}
                    </h3>
                    <p className="text-xs text-gray-500">Room Revenue</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-lg">
                    📋
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-green-600 leading-none">
                      ₹{(roomsReport.totalGSTAmount || 0).toLocaleString('en-IN')}
                    </h3>
                    <p className="text-xs text-gray-500">GST</p>
                  </div>
                </div>
              </div>

              {/* Other & Total Revenue */}
              <div className="bg-white rounded-lg border p-4 flex h-full">
                {/* Other & Total Revenue - Left Side */}
                <div className="flex flex-col justify-center pr-4">
                  {/* <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-lg">
                      ➕
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-orange-600 leading-none">
                        ₹{(roomsReport.totalOtherAmount || 0).toLocaleString('en-IN')}
                      </h3>
                      <p className="text-xs text-gray-500">Other</p>
                    </div>
                  </div> */}

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center text-lg">
                      💵
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-indigo-600 leading-none">
                        ₹{(roomsReport.totalRoomRevenue + roomsReport.totalGSTAmount + roomsReport.totalOtherAmount || 0).toLocaleString('en-IN')}
                      </h3>
                      <p className="text-xs text-gray-500">Total Revenue</p>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="w-px bg-gray-200 mx-2"></div>

                {/* Payment Method Breakdown - Right Side */}
                <div className="flex-1 pl-4">
                  <div className="text-xs font-medium text-gray-700 mb-2">Payment Methods</div>
                  <div className="space-y-1">
                    {(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.cash || 0), 0) || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Cash:</span>
                        <span className="text-xs font-semibold text-gray-800">
                          ₹{(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.cash || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                    {(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.card || 0), 0) || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">Card:</span>
                        <span className="text-xs font-semibold text-gray-800">
                          ₹{(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.card || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                    {(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.upi || 0), 0) || 0) > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-600">UPI:</span>
                        <span className="text-xs font-semibold text-gray-800">
                          ₹{(roomsReport.roomDetails?.reduce((sum, room) => sum + (room.paymentModeBreakdown?.upi || 0), 0) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>

            {/* Rooms Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room Details</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guest & Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stay Period</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GST</th>
                      {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Other Amount</th> */}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adjustments</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Final Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {roomsReport.roomDetails && roomsReport.roomDetails.length > 0 ? (
                      roomsReport.roomDetails.map((room, index) => (
                        <tr key={index} className={`hover:bg-gray-50 transition-colors duration-150 ${
                          room.status === 'cancelled' ? 'bg-red-50 border-l-4 border-red-500' : ''
                        }`}>
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900">{room.roomNumber}</div>
                              <div className="text-sm text-gray-500">{room.roomType || 'Standard'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900">{room.guestName || '-'}</div>
                              <div className="mt-1">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  room.status === 'checked_out' 
                                    ? 'bg-green-100 text-green-800' 
                                    : room.status === 'cancelled'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {room.status?.replace('_', ' ') || 'checked_out'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700">
                            <div>
                              <div>{room.checkIn ? new Date(room.checkIn).toLocaleDateString() : '-'}</div>
                              <div className="text-xs text-gray-500">to</div>
                              <div>{room.checkOut ? new Date(room.checkOut).toLocaleDateString() : '-'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-purple-600">₹{(room.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-green-600">₹{(room.gstAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </td>
                          {/* <td className="px-6 py-4">
                            <div className="font-medium text-orange-600">₹{(room.otherAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </td> */}
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="font-medium text-blue-600">
                                {(room.tipAmount || 0) > 0 
                                  ? `₹${(room.tipAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  : `${(room.tipAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                }
                              </div>
                              <div className="font-medium text-red-600">
                                {(room.discountAmount || 0) > 0 
                                  ? `-₹${(room.discountAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                  : `${(room.discountAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                }
                              </div>
                              <div className="text-xs text-gray-500 mt-1">Adjustments</div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-bold text-indigo-600">₹{(room.totalFinalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-2">
                              <div className="font-bold text-green-600 text-sm">
                                ₹{(room.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              {room.paymentModeBreakdown && (
                                <div className="space-y-1 border-t pt-1">
                                  {room.paymentModeBreakdown.cash > 0 && (
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="text-gray-600 font-medium">Cash:</span>
                                      <span className="text-gray-800 font-semibold">
                                        ₹{room.paymentModeBreakdown.cash.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                  )}
                                  {room.paymentModeBreakdown.card > 0 && (
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="text-gray-600 font-medium">Card:</span>
                                      <span className="text-gray-800 font-semibold">
                                        ₹{room.paymentModeBreakdown.card.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                  )}
                                  {room.paymentModeBreakdown.upi > 0 && (
                                    <div className="flex justify-between items-center text-xs">
                                      <span className="text-gray-600 font-medium">UPI:</span>
                                      <span className="text-gray-800 font-semibold">
                                        ₹{room.paymentModeBreakdown.upi.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                room.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                                room.paymentStatus === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {room.paymentStatus || 'pending'}
                              </span>
                              {/* <div className="relative">
                                <button 
                                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                                  title="View payment details"
                                  onClick={() => {
                                    setSelectedRoom(room);
                                    setShowPaymentModal(true);
                                  }}
                                >
                                  <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                  </svg>
                                </button>
                              </div> */}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="px-6 py-12 text-center">
                          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                            <span className="text-2xl text-gray-400">🏠</span>
                          </div>
                          <p className="text-gray-600 font-medium">No rooms checked out in the selected date range</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Payment Details Modal */}
      {showPaymentModal && selectedRoom && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Payment Details</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Room {selectedRoom.roomNumber} - {selectedRoom.guestName || 'Guest'}
                  </p>
                </div>
                <button 
                  onClick={() => setShowPaymentModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 text-sm space-y-4">

              {/* ROOM SECTION */}
              <div>
                <p className="text-xs font-semibold text-gray-400 mb-3">ROOM DETAILS</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Amount</span>
                    <span className="font-medium">₹{(selectedRoom.baseAmount || 0).toLocaleString('en-IN')}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Final Room</span>
                    <span className="font-medium">₹{(selectedRoom.finalAmount || 0).toLocaleString('en-IN')}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">GST</span>
                    <span className="font-medium text-green-600">
                      ₹{(selectedRoom.gstAmount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Other</span>
                    <span className="font-medium text-orange-600">
                      ₹{(selectedRoom.otherAmount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>

              {/* DIVIDER */}
              <div className="border-t"></div>

              {/* DISCOUNT & EXTRA */}
              <div>
                <p className="text-xs font-semibold text-gray-400 mb-3">ADJUSTMENTS</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount</span>
                    <span className="font-medium text-red-500">
                      -₹{(selectedRoom.discount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Tip</span>
                    <span className="font-medium text-blue-500">
                      ₹{(selectedRoom.tip || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>

              {/* DIVIDER */}
              <div className="border-t"></div>

              {/* PAYMENT */}
              <div>
                <p className="text-xs font-semibold text-gray-400 mb-3">PAYMENT</p>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid</span>
                    <span className="font-medium text-green-600">
                      ₹{(selectedRoom.paidAmount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Refund</span>
                    <span className="font-medium text-red-500">
                      ₹{(selectedRoom.refundableAmount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>

              {/* FINAL TOTAL */}
              <div className="border-t pt-3 flex justify-between font-semibold text-lg">
                <span>Total</span>
                <span className="text-indigo-600">
                  ₹{(selectedRoom.totalAmount || 0).toLocaleString('en-IN')}
                </span>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200">
              <button 
                onClick={() => setShowPaymentModal(false)}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorReports;
